import React from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { LinkAnchor } from "../../../components/ui/link";
import { cn } from "../../../lib/utils";
import { CONTRACTOR_SURFACE_RADIUS_MD_CLASS } from "../layoutRadius";
import type { ContractorSectionLink } from "../sections";
import type { ContractorSitePageProps } from "../types";

type SiteFooterProps = Pick<
  ContractorSitePageProps,
  "currentYear" | "rootUrl" | "business" | "seoLinks"
> & {
  sectionLinks: ContractorSectionLink[];
};

export const SiteFooter: React.FC<SiteFooterProps> = ({
  currentYear,
  rootUrl,
  business,
  sectionLinks,
  seoLinks,
}) => (
  <footer className="border-t border-border bg-card text-foreground">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
        <div className="sm:col-span-2 lg:col-span-1">
          <div className="mb-5 flex items-center gap-2">
            {business.logoUrl ? (
              <img
                src={business.logoUrl}
                alt={business.businessName}
                className="h-9 w-9 object-contain"
              />
            ) : (
              <div
                className={cn(
                  "flex h-9 w-9 items-center justify-center bg-accent",
                  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                )}
              >
                <span className="font-display text-lg font-semibold text-accent-foreground">
                  {business.businessName.charAt(0)}
                </span>
              </div>
            )}
            <span className="font-display text-xl font-semibold text-foreground">
              {business.businessName}
            </span>
          </div>
          <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
            Professional contractor services with dependable timelines and
            quality workmanship.
          </p>
        </div>

        <div>
          <h4 className="mb-5 font-display text-base font-semibold text-foreground">
            Quick Links
          </h4>
          <ul className="space-y-3">
            {sectionLinks.map((sectionLink) => (
              <li key={sectionLink.href}>
                <LinkAnchor
                  href={sectionLink.href}
                  className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                >
                  {sectionLink.label}
                </LinkAnchor>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-5 font-display text-base font-semibold text-foreground">
            Services
          </h4>
          <ul className="space-y-3">
            {business.services.slice(0, 6).map((service) => (
              <li key={service.name}>
                <LinkAnchor
                  href="#services"
                  className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                >
                  {service.name}
                </LinkAnchor>
              </li>
            ))}
            {seoLinks ? (
              <li className="pt-2">
                <LinkAnchor
                  href={seoLinks.services}
                  className="text-sm font-medium text-accent hover:underline"
                >
                  All services
                </LinkAnchor>
              </li>
            ) : null}
          </ul>
        </div>

        {seoLinks ? (
          <div>
            <h4 className="mb-5 font-display text-base font-semibold text-foreground">
              Explore
            </h4>
            <ul className="space-y-3">
              <li>
                <LinkAnchor
                  href={seoLinks.services}
                  className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                >
                  Services
                </LinkAnchor>
              </li>
              <li>
                <LinkAnchor
                  href={seoLinks.areas}
                  className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                >
                  Service Areas
                </LinkAnchor>
              </li>
              <li>
                <LinkAnchor
                  href={seoLinks.faq}
                  className="text-sm text-muted-foreground hover:text-accent hover:no-underline"
                >
                  FAQ
                </LinkAnchor>
              </li>
            </ul>
          </div>
        ) : null}

        <div className="h-card vcard">
          <h4 className="mb-5 font-display text-base font-semibold text-foreground">
            Contact Info
          </h4>
          <div className="space-y-4">
            <p className="p-name fn org text-sm font-medium text-foreground">
              {business.businessName}
            </p>
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0 text-accent" />
              <LinkAnchor
                href={`tel:${business.phone}`}
                className="p-tel tel text-sm text-muted-foreground hover:no-underline"
              >
                {business.phone}
              </LinkAnchor>
            </div>
            {business.businessEmail && (
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-accent" />
                <LinkAnchor
                  href={`mailto:${business.businessEmail}`}
                  className="u-email email text-sm text-muted-foreground hover:no-underline"
                >
                  {business.businessEmail}
                </LinkAnchor>
              </div>
            )}
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 shrink-0 text-accent" />
              <span className="p-locality locality text-sm text-muted-foreground">
                {business.serviceArea}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-border">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 sm:flex-row sm:px-6 lg:px-8">
        <p className="text-xs text-muted-foreground">
          &copy; {currentYear} {business.businessName}. All rights reserved.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
          <LinkAnchor
            href={`${rootUrl}/terms`}
            className="text-muted-foreground hover:text-foreground hover:no-underline"
          >
            Terms
          </LinkAnchor>
          <LinkAnchor
            href={`${rootUrl}/privacy`}
            className="text-muted-foreground hover:text-foreground hover:no-underline"
          >
            Privacy
          </LinkAnchor>
          <LinkAnchor
            href={`${rootUrl}/faq`}
            className="text-muted-foreground hover:text-foreground hover:no-underline"
          >
            FAQ
          </LinkAnchor>
          <span className="text-muted-foreground">·</span>
          <LinkAnchor
            href={rootUrl}
            className="text-muted-foreground hover:text-foreground hover:no-underline"
          >
            Website by ToolKit
          </LinkAnchor>
        </div>
      </div>
    </div>
  </footer>
);
